import { DEV_ONLY } from '@/config/config';

export type NavCategory = 'workspace' | 'data' | 'management' | 'company';

export interface NavItem {
  key: string;
  path: string;
  labelKey: string; // i18n translation key
  icon: string;
  category?: NavCategory; // omitted → rendered standalone above the groups
  // Access gating (see accessibleNavItems). An item shows when:
  //  - alwaysOn, or
  //  - ownerOnly and the user is an owner, or
  //  - its moduleSlug is in the token's granted ADMIN modules.
  moduleSlug?: string; // token permission slug (≠ key; snake_case from backend)
  alwaysOn?: boolean; // visible regardless of modules/role
  ownerOnly?: boolean; // visible only to company owners
}

export interface NavCategoryDef {
  key: NavCategory;
  labelKey: string; // i18n translation key for the section header
}

// Category headers, in display order. The Sidebar groups `navItems` by these.
export const navCategories: NavCategoryDef[] = [
  { key: 'workspace', labelKey: 'sidebar_group_workspace' },
  { key: 'data', labelKey: 'sidebar_group_data' },
  { key: 'management', labelKey: 'sidebar_group_management' },
  { key: 'company', labelKey: 'sidebar_group_company' },
];

// Single flat list of nav items. Items without a `category` render standalone
// (above the groups); the rest are categorized by `category` at render time.
// `moduleSlug` maps each item to its backend module slug (the join key with the
// decoded token) — icons/labels/paths stay static here; the slug only gates it.
export const navItems: NavItem[] = [
  {
    key: 'overview',
    path: '/overview',
    labelKey: 'nav_overview',
    icon: 'layout-grid',
    alwaysOn: true,
  },
  {
    key: 'projects',
    path: '/projects',
    labelKey: 'nav_projects',
    icon: 'folder-kanban',
    category: 'workspace',
    moduleSlug: 'projects',
  },
  {
    key: 'tasks',
    path: '/tasks',
    labelKey: 'nav_tasks',
    icon: 'square-check-big',
    category: 'workspace',
    moduleSlug: 'tasks',
  },
  {
    key: 'inspections',
    path: '/inspections',
    labelKey: 'nav_inspections',
    icon: 'clipboard-check',
    category: 'workspace',
    moduleSlug: 'inspections',
  },
  {
    key: 'form-templates',
    path: '/form-templates',
    labelKey: 'nav_form_templates',
    icon: 'clipboard-list',
    category: 'workspace',
    moduleSlug: 'forms_templates',
  },
  {
    key: 'attendance',
    path: '/attendance',
    labelKey: 'nav_attendance',
    icon: 'calendar-check',
    category: 'workspace',
    moduleSlug: 'attendance',
  },
  {
    key: 'tickets',
    path: '/tickets',
    labelKey: 'nav_tickets',
    icon: 'ticket',
    category: 'workspace',
    moduleSlug: 'tickets',
  },
  {
    key: 'reports',
    path: '/reports',
    labelKey: 'nav_reports',
    icon: 'file-chart-column',
    category: 'data',
    moduleSlug: 'reports',
  },
  {
    key: 'datasource',
    path: '/datasource',
    labelKey: 'nav_data_source',
    icon: 'database',
    category: 'data',
    moduleSlug: 'data_sources',
  },
  {
    key: 'data-structure',
    path: '/data-structure',
    labelKey: 'nav_data_structure',
    icon: 'library',
    category: 'data',
    moduleSlug: 'data_structure',
  },
  {
    key: 'activity-log',
    path: '/activity-log',
    labelKey: 'nav_activity_log',
    icon: 'scroll-text',
    category: 'data',
    moduleSlug: 'activity_log',
  },
  // TEMPORARY: trash is dev-only while the module is being built — same deal
  // as activity-log above. Gated by the backend's own Trash module (the trash
  // routes require the `trash` slug).
  ...(DEV_ONLY
    ? [
        {
          key: 'trash',
          path: '/trash',
          labelKey: 'nav_trash',
          icon: 'trash-2',
          category: 'company',
          moduleSlug: 'trash',
        } satisfies NavItem,
      ]
    : []),
  {
    key: 'people',
    path: '/people',
    labelKey: 'nav_people',
    icon: 'users',
    category: 'management',
    moduleSlug: 'people',
  },
  {
    key: 'structure',
    path: '/structure',
    labelKey: 'nav_structure',
    icon: 'network',
    category: 'company',
    moduleSlug: 'company_structure',
  },
  {
    key: 'data-setup',
    path: '/data-setup',
    labelKey: 'nav_data_setup',
    icon: 'database-zap',
    category: 'company',
    moduleSlug: 'dashboard_setup',
  },
  {
    key: 'roles',
    path: '/roles',
    labelKey: 'nav_roles',
    icon: 'shield-check',
    category: 'company',
    moduleSlug: 'roles',
  },
  // TEMPORARY: company settings is dev-only while the module is being built —
  // the route is generated from this list, so hiding the entry hides both.
  // Remove the DEV_ONLY gate to ship it.
  ...(DEV_ONLY
    ? [
        {
          key: 'settings',
          path: '/settings',
          labelKey: 'nav_settings',
          icon: 'settings',
          category: 'company',
          ownerOnly: true,
        } satisfies NavItem,
      ]
    : []),
];

// Dev-only navigation (rendered as its own group when DEV_ONLY is set).
export const devNav: NavItem[] = [
  { key: 'theme', path: '/theme', labelKey: 'nav_theme', icon: 'palette' },
];

// Every routed item, for the router in App.tsx.
export const navRoutes: NavItem[] = navItems;

/** Does an item pass the access gate for the given ADMIN module set + owner flag? */
const isAccessible = (item: NavItem, slugs: Set<string>, isOwner: boolean): boolean =>
  item.alwaysOn
    ? true
    : item.ownerOnly
      ? isOwner
      : item.moduleSlug
        ? slugs.has(item.moduleSlug)
        : false;

/**
 * The nav items this user may see. `adminSlugs === null` means access data was
 * absent → fail-open (show everything) so a contract hiccup can't brick the app;
 * an empty array shows only alwaysOn/owner items.
 */
export const accessibleNavItems = (adminSlugs: string[] | null, isOwner: boolean): NavItem[] => {
  if (adminSlugs === null) return navItems;
  const set = new Set(adminSlugs);
  return navItems.filter((item) => isAccessible(item, set, isOwner));
};

/** First route the user can reach — used for the default/fallback landing. */
export const firstAccessiblePath = (adminSlugs: string[] | null, isOwner: boolean): string =>
  accessibleNavItems(adminSlugs, isOwner)[0]?.path ?? '/overview';
